// client/src/contexts/ScenarioContext.tsx
"use client";

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { usePortfolio } from './PortfolioContext';
import { useAuth } from './AuthContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/utils/api';
import { Scenario, TaxActivity, TaxAdviserDetails } from '@/types';


interface ScenarioContextType {
  scenarios: Scenario[];
  loading: boolean;
  error: string | null;
  selectedScenario: string | null;
  setSelectedScenario: (id: string | null) => void;
  refreshScenarios: () => Promise<void>;
  createScenario: (data: { name: string; description?: string; is_default?: boolean; copy_from_scenario_id?: string; tax_advisor_present?: boolean; tax_activity?: TaxActivity; tax_adviser?: TaxAdviserDetails }) => Promise<Scenario>;
  updateScenario: (id: string, data: { name?: string; description?: string; is_default?: boolean; tax_advisor_present?: boolean; tax_activity?: TaxActivity; tax_adviser?: TaxAdviserDetails }) => Promise<void>;
  deleteScenario: (id: string) => Promise<void>;
  setDefaultScenario: (id: string) => Promise<void>;
  getScenario: (id: string) => Scenario | undefined;
  isInitialized: boolean;
}

const ScenarioContext = createContext<ScenarioContextType | undefined>(undefined);

async function fetchScenariosAPI(activePortfolioId: string | null | undefined): Promise<Scenario[]> {
  if (!activePortfolioId) {
    return [];
  }

  const data = await apiRequest<Scenario[]>('GET', '/scenarios', undefined, undefined, activePortfolioId);

  return data || [];
}

export function ScenarioProvider({ 
  children,
  initialScenarios = [],
  initialSelectedScenario = null
}: { 
  children: React.ReactNode;
  initialScenarios?: Scenario[];
  initialSelectedScenario?: string | null;
}) {
  
  const { activePortfolio } = usePortfolio();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // selectedScenario is persisted in localStorage per portfolio
  const [selectedScenario, setSelectedScenarioState] = useState<string | null>(initialSelectedScenario);

  // Track if user has manually selected a scenario (to avoid auto-select overwriting their choice)
  const userHasManuallySelectedRef = useRef(false);

  // Build query key that includes portfolio ID - ensures refetch when portfolio changes
  const queryKey = ['scenarios', activePortfolio?.id];

  // Use React Query for scenarios - auto-refetches when activePortfolio.id changes
  const { data: scenarios = [], isLoading, error, isFetched, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      const result = await fetchScenariosAPI(activePortfolio?.id);
      return result;
    },
    initialData: initialScenarios,
    enabled: !!user && !!activePortfolio?.id,
    staleTime: 0, // Always fetch fresh data when query key changes
    gcTime: 0, // Don't cache scenarios (they change per portfolio)
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });

  // Auto-select default scenario when scenarios are fetched
  useEffect(() => {

    if (scenarios.length > 0 && isFetched && activePortfolio?.id) {
      const defaultScenario = scenarios.find(s => s.is_default);
      const savedScenarioId = localStorage.getItem(`selectedScenario_${activePortfolio?.id}`);

      // If user has manually selected a scenario, ONLY restore from localStorage if valid
      // Don't auto-select default - preserve user's choice
      if (userHasManuallySelectedRef.current) {
        if (savedScenarioId && scenarios.some(s => s.id === savedScenarioId)) {
          // eslint-disable-next-line react-hooks/set-state-in-effect
          setSelectedScenarioState(savedScenarioId);
        }
        return; // Don't auto-select default - preserve user's manual choice
      }

      // Check if we have a saved selection for this portfolio (for first-time or returning users)
      if (savedScenarioId && scenarios.some(s => s.id === savedScenarioId)) {
        // Keep saved selection if it exists in current scenarios
         
        setSelectedScenarioState(savedScenarioId);
      } else if (defaultScenario) {
        // Auto-select default scenario

        setSelectedScenarioState(defaultScenario.id);
      } else {
        // Select first scenario

        setSelectedScenarioState(scenarios[0].id);
      }
    } else if (scenarios.length === 0 && activePortfolio?.id) {
      // If there are no scenarios for the current portfolio, reset selected scenario

      setSelectedScenarioState(null);
    }
  }, [scenarios, isFetched, activePortfolio?.id]);

  // DEL-755 (race-condition fallback): if scenarios arrive but `activePortfolio?.id`
  // is briefly undefined (initial render after a fresh login, when activePortfolio
  // is hydrated from localStorage in the same React commit as scenarios), the primary
  // effect above skips the localStorage restoration because of the falsy id guard. The
  // primary effect's deps DO include `activePortfolio?.id`, so a later re-render will
  // usually re-fire it and recover - but during that one render frame, the dashboard
  // can fetch data with the SSR-seeded `selectedScenario` while the in-context state
  // has yet to be reconciled from localStorage, briefly rendering a mix of both
  // scenarios. This effect acts as a deterministic belt-and-suspenders pass: once
  // `activePortfolio?.id` lands AND `selectedScenario` is still null (i.e., the primary
  // effect didn't restore), re-read localStorage and pick up the saved value.
  // Gated on `!selectedScenario` so it never overwrites a value the primary effect set.
  useEffect(() => {
    if (!activePortfolio?.id) return;
    if (scenarios.length === 0 || !isFetched) return;
    if (selectedScenario !== null) return;

    const savedScenarioId = localStorage.getItem(`selectedScenario_${activePortfolio.id}`);
    if (savedScenarioId && scenarios.some((s) => s.id === savedScenarioId)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedScenarioState(savedScenarioId);
    }
  }, [activePortfolio?.id, scenarios, isFetched, selectedScenario]);

  // Handle portfolio change event to ensure scenarios are refreshed
  useEffect(() => {
    const handlePortfolioChange = () => {
      console.log('[ScenarioContext] Portfolio changed event received');
      // The query will automatically refetch due to the query key changing,
      // but we also need to ensure the selected scenario is updated appropriately
    };

    window.addEventListener('portfolio-changed', handlePortfolioChange);

    return () => {
      window.removeEventListener('portfolio-changed', handlePortfolioChange);
    };
  }, []);

  // Persist selected scenario to localStorage when it changes
  const setSelectedScenario = useCallback((id: string | null) => {
    if (activePortfolio?.id) {
      localStorage.setItem(`selectedScenario_${activePortfolio?.id}`, id || '');
    }
    // Mark that user has manually selected a scenario (so auto-select doesn't overwrite)
    userHasManuallySelectedRef.current = true;
    setSelectedScenarioState(id);
  }, [activePortfolio?.id]);

  // Refresh scenarios manually
  const refreshScenarios = useCallback(async () => {
    await refetch();
  }, [refetch]);

  // Get a scenario by ID
  const getScenario = useCallback((id: string) => {
    return scenarios.find(s => s.id === id);
  }, [scenarios]);

  // Create a new scenario
  const createScenario = useCallback(async (data: {
    name: string;
    description?: string;
    is_default?: boolean;
    copy_from_scenario_id?: string;
    tax_advisor_present?: boolean;
    tax_activity?: TaxActivity;
    tax_adviser?: TaxAdviserDetails;
  }) => {
    if (!activePortfolio) {
      throw new Error('No active portfolio');
    }

    const created = await apiRequest<Scenario>('POST', '/scenarios', data, undefined, activePortfolio.id);

    // Invalidate scenarios query to refetch with new data
    queryClient.invalidateQueries({ queryKey: ['scenarios'] });

    return created;
  }, [activePortfolio, queryClient]);

  // Update a scenario
  const updateScenario = useCallback(async (id: string, data: {
    name?: string;
    description?: string;
    is_default?: boolean;
    tax_advisor_present?: boolean;
    tax_activity?: TaxActivity;
    tax_adviser?: TaxAdviserDetails;
  }) => {
    if (!activePortfolio) {
      throw new Error('No active portfolio');
    }

    await apiRequest('PUT', `/scenarios/${id}`, data, undefined, activePortfolio.id);

    // Invalidate scenarios query to refetch with new data
    queryClient.invalidateQueries({ queryKey: ['scenarios'] });
  }, [activePortfolio, queryClient]);

  // Delete a scenario
  const deleteScenario = useCallback(async (id: string) => {
    if (!activePortfolio) {
      throw new Error('No active portfolio');
    }

    await apiRequest('DELETE', `/scenarios/${id}`, undefined, undefined, activePortfolio.id);

    // Clear selected scenario if it was deleted
    if (selectedScenario === id) {
      setSelectedScenario(null);
    }

    // Invalidate scenarios query to refetch with new data
    queryClient.invalidateQueries({ queryKey: ['scenarios'] });
  }, [activePortfolio, selectedScenario, setSelectedScenario, queryClient]);

  // Set a scenario as default
  const setDefaultScenario = useCallback(async (id: string) => {
    if (!activePortfolio) {
      throw new Error('No active portfolio');
    }

    // The API has no /set-default route (it 404'd on every click). PUT /scenarios/:id
    // with is_default: true sets this one and clears the portfolio's previous default.
    await apiRequest('PUT', `/scenarios/${id}`, { is_default: true }, undefined, activePortfolio.id);

    // Update selected scenario
    setSelectedScenario(id);

    // Invalidate scenarios query to refetch with new data
    queryClient.invalidateQueries({ queryKey: ['scenarios'] });
  }, [activePortfolio, setSelectedScenario, queryClient]);

  const value: ScenarioContextType = {
    scenarios,
    loading: isLoading,
    error: error ? (error instanceof Error ? error.message : 'Failed to fetch scenarios') : null,
    selectedScenario,
    setSelectedScenario,
    refreshScenarios,
    createScenario,
    updateScenario,
    deleteScenario,
    setDefaultScenario,
    getScenario,
    isInitialized: isFetched
  };

  return (
    <ScenarioContext.Provider value={value}>
      {children}
    </ScenarioContext.Provider>
  );
}

export function useScenarios() {
  const context = useContext(ScenarioContext);
  if (context === undefined) {
    throw new Error('useScenarios must be used within a ScenarioProvider');
  }
  return context;
}
